import { FunctionComponent, ReactNode } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";

interface SettingsCardProps {
	/** Icon shown in the indigo-soft badge in the card header (mockup `bh-ico`). */
	icon: IconDefinition;
	title: string;
	subtitle?: string;
	/** Optional element pinned to the right of the header (e.g. a "Connected" badge). */
	headerRight?: ReactNode;
	/** Optional footer bar (e.g. Cancel / Save changes), rendered on a tinted strip. */
	footer?: ReactNode;
	children: ReactNode;
}

/**
 * Shared card shell for the Account settings pages. Mirrors the mockup's `.box`:
 * a header row with an indigo-soft icon badge + title/subtitle, the body, and an
 * optional tinted footer bar. The body owns its own padding so cards can lay out
 * rows/forms freely.
 */
export const SettingsCard: FunctionComponent<SettingsCardProps> = ({
	icon,
	title,
	subtitle,
	headerRight,
	footer,
	children,
}) => (
	<div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
		<div className="flex items-start gap-3 border-b border-gray-100 px-5 py-4">
			<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[9px] bg-blue-action-200 text-brand-light">
				<Icon className="h-[18px] w-[18px]" icon={icon} />
			</div>

			<div className="min-w-0 flex-1">
				<h6 className="text-[15px] font-bold text-text">{title}</h6>
				{subtitle && <p className="mt-0.5 text-[12.5px] text-text-meta">{subtitle}</p>}
			</div>

			{headerRight && <div className="flex-shrink-0">{headerRight}</div>}
		</div>

		<div className="flex-1">{children}</div>

		{footer && (
			<div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-100/50 px-5 py-3">
				{footer}
			</div>
		)}
	</div>
);
